import React, { useEffect, useMemo, useState } from 'react';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { validateMovieForm } from '../../validation/hobbies';
import useStepperFlow from '../../hooks/useStepperFlow';
import { includesCollectionName, normalizeCollectionOptions, resolveCollectionMode } from '../../utils/collectionOptions';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';

const emptyMovie = {
  id: null,
  title: '',
  releaseYear: '',
  posterUrl: '',
  director: '',
  cast: '',
  genres: '',
  runtime: '',
  status: 'Want to watch',
  rating: '',
  favorite: false,
  watchedDate: '',
  rewatch: false,
  review: '',
  notes: '',
  spoiler: false,
  collection: 'My Movies',
  whereWatched: '',
};
const stepErrorFields = {
  details: ['title', 'releaseYear', 'posterUrl', 'director', 'cast', 'genres', 'runtime'],
  library: ['status', 'collection', 'whereWatched', 'watchedDate', 'favorite'],
  review: ['rating', 'review', 'spoiler'],
};

const whereWatchedOptions = [
  'Theater',
  'Netflix',
  'Prime Video',
  'Hulu',
  'Disney+',
  'Max',
  'Apple TV+',
  'YouTube',
  'Owned / Rental',
  'Other',
];

/**
 * Performs the where watched label operation.
 *
 * @param {string} status - status value.
 * @returns {*} The derived operation result.
 */
function whereWatchedLabel(status) {
  return ['Watched', 'Abandoned'].includes(status)
    ? mediaStrings.movies.form.fields.watchedOn
    : mediaStrings.movies.form.fields.watchOn;
}

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return { ...emptyMovie };

  return {
    ...emptyMovie,
    ...initialData,
    cast: Array.isArray(initialData.cast) ? initialData.cast.join(', ') : initialData.cast || '',
    genres: Array.isArray(initialData.genres) ? initialData.genres.join(', ') : initialData.genres || '',
  };
}

/**
 * Renders the star rating experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function StarRating({ value, onChange }) {
  const rating = Number(value || 0);
  const stars = Array.from({ length: 5 }, (_, index) => index + 1);

  /**
   * Retrieves star state.
   *
   * @param {*} star - star value.
   * @returns {*} The derived operation result.
   */
  const getStarState = (star) => {
    if (rating >= star) return 'full';
    if (rating >= star - 0.5) return 'half';
    return 'empty';
  };

  return (
    <div className="star-rating-wrap">
      <div className="star-rating" role="radiogroup" aria-label={mediaStrings.movies.form.ratingLabel}>
        {stars.map((star) => (
          <span className={`star-button ${getStarState(star)}`} key={star}>
            <span className="star-icon" aria-hidden="true">★</span>
            <button
              type="button"
              className="star-hit star-hit-left"
              onClick={() => onChange(String(star - 0.5))}
              aria-pressed={rating === star - 0.5}
              aria-label={mediaStrings.movies.form.starLabel(star - 0.5)}
            />
            <button
              type="button"
              className="star-hit star-hit-right"
              onClick={() => onChange(String(star))}
              aria-pressed={rating === star}
              aria-label={mediaStrings.movies.form.starLabel(star)}
            />
          </span>
        ))}
      </div>
      <div className="star-rating-summary">
        <span>{rating ? mediaStrings.movies.form.ratingValue(rating) : mediaStrings.movies.form.notRated}</span>
        {rating > 0 && (
          <button type="button" className="btn-ghost compact-btn" onClick={() => onChange('')}>
            {mediaStrings.movies.form.clearRating}
          </button>
        )}
      </div>
    </div>
  );
}

/** Collects library and review metadata for a selected or manually entered movie. */
export default function MoviesForm({ theme, onSave, onCancel, isSaving, initialData, onDirtyChange, watchlists = [], isEditing = false }) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whereWatchedMode, setWhereWatchedMode] = useState('');
  const steps = useMemo(() => ['details', 'library', 'review'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'details');
  const watchlistOptions = useMemo(
    () => normalizeCollectionOptions(watchlists),
    [watchlists]
  );
  const isKnownWatchlist = includesCollectionName(watchlistOptions, form.collection);
  const [watchlistMode, setWatchlistMode] = useState('existing');

  useEffect(() => {
    setForm(initialForm);
    resetStepper('details');
    setWhereWatchedMode(
      whereWatchedOptions.includes(initialForm.whereWatched)
        ? initialForm.whereWatched
        : initialForm.whereWatched ? 'Other' : ''
    );
  }, [initialForm, resetStepper]);

  useEffect(() => {
    if (!watchlistOptions.length) {
      setWatchlistMode('new');
      return;
    }
    setWatchlistMode(resolveCollectionMode({
      options: watchlistOptions,
      value: initialForm.collection,
      defaultValue: emptyMovie.collection,
    }));
  }, [initialForm.collection, watchlistOptions]);

  useEffect(() => {
    if (watchlistMode === 'existing' && watchlistOptions.length && !includesCollectionName(watchlistOptions, form.collection)) {
      updateField('collection', watchlistOptions[0]);
    }
  }, [form.collection, watchlistMode, watchlistOptions]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm));
  }, [form, initialForm, onDirtyChange]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /**
   * Updates where watched mode.
   *
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateWhereWatchedMode = (value) => {
    setWhereWatchedMode(value);
    updateField('whereWatched', value === 'Other' ? '' : value);
  };

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    if (watchlistMode === 'new' && includesCollectionName(watchlistOptions, form.collection)) {
      const nextErrors = { collection: mediaStrings.movies.form.errors.collectionDuplicate };
      setErrors((current) => ({ ...current, ...nextErrors }));
      showFirstStepWithErrors(nextErrors, stepErrorFields, 'library', goToSection);
      return;
    }
    const validation = validateMovieForm(form);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'details', goToSection);
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="recipe-form movie-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="Movie editor steps">
        {steps.map((section, index) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : index < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{index + 1}</span>
            {mediaStrings.movies.form.sections[section] || section}
          </button>
        ))}
      </div>

      {activeSection === 'details' && (
      <section className="recipe-form-section priority-section">
        <h3>{mediaStrings.movies.form.sections.movie}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.title}</label>
            <input
              type="text"
              aria-invalid={Boolean(errors.title)}
              value={form.title}
              onChange={(event) => updateField('title', event.target.value)}
              placeholder={mediaStrings.movies.form.placeholders.title}
              disabled={isEditing}
              className={isEditing ? 'locked-field-input' : ''}
            />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>

          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.releaseYear}</label>
            <input
              value={form.releaseYear}
              onChange={(event) => updateField('releaseYear', event.target.value)}
              placeholder={mediaStrings.movies.form.placeholders.releaseYear}
              inputMode="numeric"
              aria-invalid={Boolean(errors.releaseYear)}
            />
            {errors.releaseYear && <p className="field-error">{errors.releaseYear}</p>}
          </div>

          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.runtime}</label>
            <input
              value={form.runtime}
              onChange={(event) => updateField('runtime', event.target.value)}
              placeholder={mediaStrings.movies.form.placeholders.runtime}
            />
          </div>

          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.director}</label>
            <input
              value={form.director}
              onChange={(event) => updateField('director', event.target.value)}
              placeholder={mediaStrings.movies.form.placeholders.director}
            />
          </div>

          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.genres}</label>
            <input
              value={form.genres}
              onChange={(event) => updateField('genres', event.target.value)}
              placeholder={mediaStrings.movies.form.placeholders.genres}
            />
          </div>

          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.cast}</label>
            <input
              value={form.cast}
              onChange={(event) => updateField('cast', event.target.value)}
              placeholder={mediaStrings.movies.form.placeholders.cast}
            />
          </div>

        </div>
      </section>
      )}

      {activeSection === 'library' && (
      <section className="recipe-form-section">
        <h3>{mediaStrings.movies.form.sections.library}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.status}</label>
            <select value={form.status} onChange={(event) => updateField('status', event.target.value)} aria-invalid={Boolean(errors.status)}>
              {mediaStrings.movies.form.options.statuses.map((status) => <option key={status}>{status}</option>)}
            </select>
            {errors.status && <p className="field-error">{errors.status}</p>}
          </div>

          <div className="form-group">
            <label>{mediaStrings.movies.form.fields.collection}</label>
            <div className="collection-picker">
              {watchlistOptions.length > 0 && (
                <div className="collection-mode-toggle" role="group" aria-label={mediaStrings.movies.form.options.watchlistMode}>
                  <button
                    type="button"
                    className={watchlistMode === 'existing' ? 'active' : ''}
                    onClick={() => {
                      setWatchlistMode('existing');
                      updateField('collection', isKnownWatchlist ? form.collection : watchlistOptions[0]);
                    }}
                  >
                    {mediaStrings.movies.form.options.existing}
                  </button>
                  <button
                    type="button"
                    className={watchlistMode === 'new' ? 'active' : ''}
                    onClick={() => {
                      setWatchlistMode('new');
                      if (isKnownWatchlist) updateField('collection', '');
                    }}
                  >
                    {mediaStrings.movies.form.options.createNew}
                  </button>
                </div>
              )}
              {watchlistMode === 'existing' && watchlistOptions.length > 0 ? (
                <select value={isKnownWatchlist ? form.collection : watchlistOptions[0]} onChange={(event) => updateField('collection', event.target.value)}>
                  {watchlistOptions.map((watchlist) => <option key={watchlist} value={watchlist}>{watchlist}</option>)}
                </select>
              ) : (
                <input
                  value={form.collection}
                  onChange={(event) => updateField('collection', event.target.value)}
                  placeholder={mediaStrings.movies.form.placeholders.collection}
                />
              )}
            </div>
            {errors.collection && <p className="field-error">{errors.collection}</p>}
          </div>

          <div className="form-group">
            <label>{whereWatchedLabel(form.status)}</label>
            <select
              value={whereWatchedMode}
              onChange={(event) => updateWhereWatchedMode(event.target.value)}
            >
              <option value="">{mediaStrings.movies.form.options.notSelected}</option>
              {whereWatchedOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>

          {whereWatchedMode === 'Other' && (
            <div className="form-group">
              <label>{mediaStrings.movies.form.fields.otherLocation}</label>
              <input
                value={form.whereWatched}
                onChange={(event) => updateField('whereWatched', event.target.value)}
                placeholder={mediaStrings.movies.form.placeholders.otherLocation}
              />
            </div>
          )}

          {form.status === 'Watched' && (
            <div className="form-group">
              <label>{mediaStrings.movies.form.fields.watchedDate}</label>
              <input
                type="date"
                value={form.watchedDate}
                onChange={(event) => updateField('watchedDate', event.target.value)}
                aria-invalid={Boolean(errors.watchedDate)}
              />
              {errors.watchedDate && <p className="field-error">{errors.watchedDate}</p>}
            </div>
          )}

          <div className="form-group form-grid-full">
            <button
              type="button"
              className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
              onClick={() => updateField('favorite', !form.favorite)}
              aria-pressed={form.favorite}
            >
              <span>{form.favorite ? mediaStrings.movies.form.favorite : mediaStrings.movies.form.favoriteAction}</span>
            </button>
          </div>
        </div>
      </section>
      )}

      {activeSection === 'review' && (
      <section className="recipe-form-section">
        <h3>{mediaStrings.movies.form.sections.review}</h3>
        <div className="form-grid two-columns">
          <div className="form-group form-grid-full">
            <label>{mediaStrings.movies.form.fields.rating}</label>
            <StarRating value={form.rating} onChange={(value) => updateField('rating', value)} />
            {errors.rating && <p className="field-error">{errors.rating}</p>}
          </div>

          <div className="form-group form-grid-full">
            <label className="checkbox-card inline-checkbox-card">
              <input
                type="checkbox"
                checked={form.spoiler}
                onChange={(event) => updateField('spoiler', event.target.checked)}
              />
              <span>{mediaStrings.movies.form.options.spoiler}</span>
            </label>
          </div>

          <div className="form-group form-grid-full">
            <label>{mediaStrings.movies.form.fields.review}</label>
            <textarea
              rows="5"
              value={form.review}
              onChange={(event) => updateField('review', event.target.value)}
            />
          </div>
        </div>
      </section>
      )}

      <div className="form-actions sticky-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>
          {moduleCommonStrings.actions.cancel}
        </button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={handleSubmit} disabled={isSaving || isSubmitting || !isStepSettled}>
              {isSaving || isSubmitting ? moduleCommonStrings.actions.saving : mediaStrings.movies.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
